import type { SourceGroup as CatalogSourceGroup } from 'features/catalog/types'
import type { FocusListOption } from 'features/catalog/types'
import type { FilterOptionValue } from 'features/filters/types'
import type { SourceGroup } from 'features/sourcepage/types'

// ---------------------------------------------------------------------------
// Pure per-lens row-building transforms, extracted from the removed
// `catalog-list/CatalogList.tsx` (its `data`/`processedData` memos) with no
// behavior change. The input `FilterOptionValue[]` for the select-shaped
// lenses (brand/theme/source_type/product_hierarchy) now comes from the
// panel-scoped `/post/filter/options/v2` response (via `useEffectiveFilterList`
// in `use-catalog-table-data.ts`) instead of the raw project-wide
// `defaultFilterList` — so `n_posts` on each value is filtered by every
// sidebar filter, not just the date range. The enrichment queries
// (`getProductHierarchiesForProject`/`getSourceGroups`) stay date-only
// scoped (a backend limitation, not fixed here) and are left-joined by value
// for their extra columns (thumbnail, product_count, source_urls, country).
// ---------------------------------------------------------------------------

export type HierarchyEnrichmentRow = {
  hierarchy: string
  review_count: number
  image_url?: string | null
  product_count?: number
  source_urls?: string[]
}

export function buildProductHierarchyRows(
  filterValues: FilterOptionValue[],
  enrichment: HierarchyEnrichmentRow[] | undefined,
): FocusListOption[] {
  const byHierarchy = new Map((enrichment ?? []).map((h) => [h.hierarchy, h]))
  const seen = new Set<string>()
  const rows: FocusListOption[] = []
  for (const v of filterValues) {
    const enrich = byHierarchy.get(v.value)
    seen.add(v.value)
    rows.push({
      value: v.value,
      n_posts: v.n_posts ?? 0,
      identifiers: [v.value],
      image_url: enrich?.image_url ?? undefined,
      product_count: enrich?.product_count,
      source_urls: enrich?.source_urls,
    })
  }
  // Zero-record scoped hierarchies live only in enrichment — the superset
  // filter cache is built from `analytics_<hub_id>` and misses them.
  for (const h of enrichment ?? []) {
    if (seen.has(h.hierarchy)) continue
    rows.push({
      value: h.hierarchy,
      n_posts: 0,
      identifiers: [h.hierarchy],
      image_url: h.image_url ?? undefined,
      product_count: h.product_count,
      source_urls: h.source_urls,
    })
  }
  return rows
}

export function buildSourceRows(
  filterValues: FilterOptionValue[],
  sourceData: CatalogSourceGroup[] | undefined,
  groupCountries: boolean,
  hasActiveFilter: boolean,
): FocusListOption[] {
  const hasCountryBreakdown =
    sourceData && sourceData.some((r) => r.country !== undefined)

  if (!hasCountryBreakdown || !sourceData) {
    // Fallback: one entry per source name, no country breakdown available.
    return filterValues.map((source) => {
      const matchingSource = sourceData?.find(
        (el) => el.source === source.value,
      )
      return {
        ...source,
        n_posts: source.n_posts ?? 0,
        image_url: matchingSource?.source,
        product_count: matchingSource?.nsources,
      }
    })
  }

  // Group sourceData rows by source name.
  const bySource: Record<
    string,
    { reviews: number; nsources: number; countries: string[] }
  > = {}
  for (const row of sourceData) {
    const key = row.source
    const countryLabel = row.country || 'Unknown'
    if (!bySource[key]) {
      bySource[key] = { reviews: 0, nsources: 0, countries: [] }
    }
    bySource[key]!.reviews += row.reviews
    bySource[key]!.nsources += row.nsources
    if (!bySource[key]!.countries.includes(countryLabel)) {
      bySource[key]!.countries.push(countryLabel)
    }
  }
  // Sort each source's country list alphabetically, promoting "Unknown" last.
  for (const agg of Object.values(bySource)) {
    agg.countries.sort((a, b) => {
      if (a === 'Unknown') return 1
      if (b === 'Unknown') return -1
      return a.localeCompare(b)
    })
  }

  if (groupCountries) {
    // One entry per source name; counts summed across its countries.
    // `n_posts` is the panel-filtered count (from `filterValues`), never the
    // date-only `sourceData` sum — `sourceData` here is enrichment only
    // (country list, source count), same contract as every other lens.
    return filterValues.map((source) => {
      const agg = bySource[source.value]
      return {
        ...source,
        n_posts: source.n_posts ?? 0,
        image_url: source.value,
        product_count: agg?.nsources,
        countries: agg?.countries ?? [],
      }
    })
  }

  // One entry per (source name, country); value is unique per pair. Per-country
  // counts stay date-only (no panel-scoped per-country breakdown exists), but a
  // source whose own panel-filtered total doesn't clear an active filter must
  // still disappear entirely — otherwise its date-only country rows survive
  // regardless of the filter (the bug this drop closes).
  const entries: FocusListOption[] = []
  for (const source of filterValues) {
    if (hasActiveFilter && (source.n_posts ?? 0) <= 0) {
      continue
    }
    const rows = sourceData.filter((r) => r.source === source.value)
    if (rows.length === 0) {
      // No country breakdown available; fall back to a single entry.
      entries.push({
        ...source,
        n_posts: source.n_posts ?? 0,
        image_url: source.value,
      })
    } else {
      for (const row of rows) {
        const countryLabel = row.country || 'Unknown'
        entries.push({
          value: `${source.value} · ${countryLabel}`,
          n_posts: row.reviews,
          identifiers: source.identifiers ?? [source.value],
          image_url: source.value,
          product_count: row.nsources,
          country: countryLabel,
          sourceName: source.value,
        })
      }
    }
  }
  return entries
}

/** The two synthetic theme buckets that aren't real themes — dropped by both
 * theme row builders below. */
const THEME_SYNTHETIC_BUCKETS = new Set([
  'Critics / Detractors',
  'Fans / Attractors',
])

/** Flat, one row per individual (sub)theme value — no aggregation. Used when
 * the "Group themes" toggle is off, so every subtheme ("Scent: Floral",
 * "Scent: Fresh") is its own browsable row instead of summed into its
 * parent. */
export function buildUngroupedThemeRows(
  filterValues: FilterOptionValue[],
): FocusListOption[] {
  return filterValues
    .filter(
      (v) => !THEME_SYNTHETIC_BUCKETS.has(v.value.split(': ')[0] ?? v.value),
    )
    .map((v) => ({
      ...v,
      n_posts: v.n_posts ?? 0,
      identifiers: [v.cluster_uuid ?? v.value],
    }))
}

/** Groups theme values on the `"Name: Subname"` prefix (the parent theme
 * name), summing counts, and — only when a parent has more than one
 * subtheme — attaches them as `children` (antd's row-expand affordance),
 * each with the `"Parent: "` prefix stripped into `displayLabel`. Drops the
 * two synthetic buckets that aren't real themes. Used when "Group themes" is
 * on (the default); mirrors `SummaryMetrics.buildGroupedRowsWithChildren`'s
 * theme branch (same `children.length > 1` gate, same prefix stripping) so
 * catalog's grouped view matches it exactly. */
export function buildGroupedThemeRowsWithChildren(
  filterValues: FilterOptionValue[],
): FocusListOption[] {
  const byParent = new Map<string, FilterOptionValue[]>()
  for (const el of filterValues) {
    const groupName = el.value.split(': ')[0] ?? el.value
    if (THEME_SYNTHETIC_BUCKETS.has(groupName)) continue
    const siblings = byParent.get(groupName) ?? []
    siblings.push(el)
    byParent.set(groupName, siblings)
  }

  const rows: FocusListOption[] = []
  for (const [groupName, values] of byParent) {
    const parentRow: FocusListOption = {
      value: groupName,
      n_posts: values.reduce((sum, v) => sum + (v.n_posts ?? 0), 0),
      identifiers: values.map((v) => v.cluster_uuid ?? v.value),
    }

    // Multiple raw values can share the same subtheme name once the parent
    // prefix is stripped (distinct cluster_uuids the backend labels
    // identically) — merge them into one child (sum n_posts, collect
    // identifiers) instead of listing the same subtheme repeatedly.
    const byChild: Record<string, FocusListOption & { identifiers: string[] }> =
      {}
    for (const v of values) {
      const displayLabel = v.value.split(': ')[1] ?? v.value
      const existing = byChild[displayLabel]
      if (existing) {
        existing.n_posts += v.n_posts ?? 0
        existing.identifiers.push(v.cluster_uuid ?? v.value)
      } else {
        byChild[displayLabel] = {
          ...v,
          n_posts: v.n_posts ?? 0,
          identifiers: [v.cluster_uuid ?? v.value],
          displayLabel,
          parentValue: groupName,
        }
      }
    }
    const children = Object.values(byChild)

    if (children.length > 1) {
      parentRow.children = children
    }
    rows.push(parentRow)
  }
  return rows
}

/** Syndicated sources aren't a `defaultFilterList` field — there is no
 * filterable "syndicated source" vocabulary the panel's other filters could
 * narrow — so this row set is built directly from the dedicated
 * date-scoped endpoint (`getSourceGroups('syndicated_source_group', ...)`)
 * rather than joined against panel-scoped filter values like every other
 * lens/sub-set. Each row is marked `syndicated: true` so the table can badge
 * it; detail-page navigation treats it identically to any other source row. */
export function buildSyndicatedSourceRows(
  syndicatedSourceData: SourceGroup[] | undefined,
): FocusListOption[] {
  return (syndicatedSourceData ?? []).map((row) => ({
    value: row.source,
    n_posts: row.reviews,
    identifiers: [row.source],
    image_url: row.source,
    product_count: row.nsources,
    syndicated: true,
  }))
}

export function buildBrandRows(
  filterValues: FilterOptionValue[],
): FocusListOption[] {
  return filterValues.map((v) => ({ ...v, n_posts: v.n_posts ?? 0 }))
}

/**
 * Builds the table's row set for one lens, then — only once a real sidebar
 * filter or search is active (`hasActiveFilter`) — drops rows whose count
 * fell to 0 under it. `useEffectiveFilterList`'s value list is always the
 * FULL project-wide vocabulary with panel-scoped counts overlaid (correct
 * for its original job, a filter-value picker, where a 0-count option
 * should stay visible); a browsable table needs this extra step so applying
 * a filter actually narrows what's shown. With no filter active, every row
 * stays — including a legitimately-tracked 0-post one (PROD-5288's tracking
 * banner). Syndicated source rows are exempt: they aren't part of the
 * filterable vocabulary at all (see `buildSyndicatedSourceRows`), so their
 * count is never affected by other filters either way.
 *
 * This drop only works because the caller fetches `filterValues` with
 * `faceted: false` (`useCatalogTableRows`) — a faceted fetch strips the
 * selected lens's own filter before counting it, so e.g. a `brand is Amika`
 * filter on the Brands view would leave every non-Amika brand at its
 * unfiltered count and this function would drop nothing. */
export function assembleCatalogTableRows(params: {
  selectedLens: string
  filterValues: FilterOptionValue[]
  hierarchyData: HierarchyEnrichmentRow[] | undefined
  sourceData: CatalogSourceGroup[] | undefined
  syndicatedSourceData: SourceGroup[] | undefined
  groupCountries: boolean
  groupThemes: boolean
  hasActiveFilter: boolean
}): FocusListOption[] {
  const {
    selectedLens,
    filterValues,
    hierarchyData,
    sourceData,
    syndicatedSourceData,
    groupCountries,
    groupThemes,
    hasActiveFilter,
  } = params

  let filterable: FocusListOption[]
  switch (selectedLens) {
    case 'product_hierarchy':
      filterable = buildProductHierarchyRows(filterValues, hierarchyData)
      break
    case 'source_type':
      filterable = buildSourceRows(
        filterValues,
        sourceData,
        groupCountries,
        hasActiveFilter,
      )
      break
    case 'theme':
      filterable = groupThemes
        ? buildGroupedThemeRowsWithChildren(filterValues)
        : buildUngroupedThemeRows(filterValues)
      break
    default:
      filterable = buildBrandRows(filterValues)
  }
  if (hasActiveFilter) {
    filterable = filterable.filter((row) => row.n_posts > 0)
  }

  const built =
    selectedLens === 'source_type'
      ? [...filterable, ...buildSyndicatedSourceRows(syndicatedSourceData)]
      : filterable

  return aggregateCatalogRows(built)
}

/** Dedupe-by-value and sum counts/identifiers for any duplicate `value` keys
 * the API returns (mirrors the removed CatalogList's `processedData` memo,
 * minus its sort — sorting is now the table's column sorters). */
export function aggregateCatalogRows(
  rows: FocusListOption[],
): FocusListOption[] {
  const dedupedValues: Record<string, FocusListOption> = {}
  rows.forEach((item) => {
    const existing = dedupedValues[item.value]
    if (!existing) {
      dedupedValues[item.value] = {
        ...item,
        identifiers: item?.identifiers ?? [item.value],
      }
    } else {
      existing.n_posts += item.n_posts
      if (item.product_count) {
        existing.product_count =
          (existing.product_count ?? 0) + item.product_count
      }
      existing.identifiers = existing.identifiers.concat(item.identifiers ?? [])
      if (item.source_urls) {
        existing.source_urls = (existing.source_urls ?? []).concat(
          item.source_urls,
        )
      }
    }
  })
  return Object.values(dedupedValues)
}
